import { Container, ContainerChild, Sprite, Texture } from "pixi.js";

import type { ChatMessage } from "../../../../services/eventBus/types";

import {
  renderChatDesignSvg,
  type ChatDesign,
} from "../../../../data/chatDesignsCatalog";

import {
  ensureDesignFontsReady,
  rasterizeSvgToTexture,
} from "./svgTextureRaster";

/** Min ms between re-rasters — coalesces chat bursts to ≤4 redraws/sec. */
const MIN_RASTER_MS = 250;

/**
 * Design-store render strategy for the Chat widget — the message-list sibling
 * of `FollowerGoalDesignRenderer`. Renders the chosen design's PANEL SVG (with
 * the latest N messages laid out per the design) as a single Pixi sprite
 * stretched to the widget bounds.
 *
 * Chat can burst (dozens of messages/sec in a raid), so unlike the other
 * design renderers this one COALESCES re-rasters: it keys on the visible
 * message-id window (skips redundant redraws) and trailing-throttles to at most
 * one raster per {@link MIN_RASTER_MS}, always rendering the latest pending set.
 *
 * The parent `ChatWidgetRenderer` owns the `eventBusService.onChat`
 * subscription, the platform/blocked/slow-mode filtering and the per-widget
 * buffer; it just calls {@link update} with the current message buffer. One
 * instance per widget (kept in the parent's Map).
 */
export class ChatDesignRenderer {
  private container: Container | null = null;

  private sprite: Sprite | null = null;

  private destWidth = 1;

  private destHeight = 1;

  private renderScale = 1;

  /** Key of the visible message window baked into the current texture. */
  private lastKey: string | null = null;

  /** Latest messages awaiting a (possibly throttled) raster. */
  private pending: ChatMessage[] | null = null;

  /** Wall-clock of the last raster start (for the trailing throttle). */
  private lastRasterAt = 0;

  /** Trailing-throttle timer; non-null while a coalesced raster is queued. */
  private throttleTimer: ReturnType<typeof setTimeout> | null = null;

  private texture: Texture | null = null;

  /** Bumped per re-raster so a slow async decode can't apply out of order. */
  private generation = 0;

  private destroyed = false;

  constructor(private readonly design: ChatDesign) {}

  /** Presupuesto de filas del diseño (para armar el input con los pines). */
  get maxRows(): number {
    return this.design.maxRows;
  }

  render(
    parentContainer: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    this.container = new Container();
    parentContainer.addChild(this.container);

    this.destWidth = Math.max(1, destination.width);
    this.destHeight = Math.max(1, destination.height);

    const scaleForBounds = Math.max(
      1,
      this.destWidth / this.design.cardWidth,
      this.destHeight / this.design.cardHeight,
    );
    this.renderScale = Math.min(scaleForBounds, 3);

    const sprite = new Sprite(Texture.EMPTY);
    sprite.width = this.destWidth;
    sprite.height = this.destHeight;
    sprite.position.set(0, 0);
    this.container.addChild(sprite as unknown as ContainerChild);
    this.sprite = sprite;

    // Preload the design's Google fonts, then redraw the latest pending set so
    // the real typeface (not a system fallback) is baked in. `fonts` puede venir
    // ausente en un payload de la comunidad (el registro lo normaliza, pero el
    // consumidor no depende de eso).
    void ensureDesignFontsReady(this.design.fonts ?? []).then(() => {
      if (this.destroyed) return;
      this.lastKey = null;
      if (this.pending) void this.rerasterize(this.pending);
    });
  }

  /**
   * Push the current message buffer. Re-rasterises only when the visible
   * window (latest `maxRows` ids) changes, trailing-throttled to MIN_RASTER_MS.
   */
  update(messages: ChatMessage[]): void {
    if (this.destroyed || !this.sprite) return;
    // `maxRows <= 0` (diseño de la comunidad sin `list`) ⇒ buffer completo,
    // el mismo criterio que `chatLinesForDesign` en el editor: que la paridad
    // no dependa de la sutileza de `slice(-0)`.
    const recent =
      this.design.maxRows > 0
        ? messages.slice(-this.design.maxRows)
        : messages;
    const key = `${recent.length}:${recent.map((m) => m.id).join("|")}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.pending = recent;
    this.scheduleRaster();
  }

  private scheduleRaster(): void {
    if (this.throttleTimer || this.destroyed) return;
    const since =
      typeof performance !== "undefined"
        ? performance.now() - this.lastRasterAt
        : MIN_RASTER_MS;
    if (since >= MIN_RASTER_MS) {
      this.flush();
    } else {
      this.throttleTimer = setTimeout(() => {
        this.throttleTimer = null;
        this.flush();
      }, MIN_RASTER_MS - since);
    }
  }

  private flush(): void {
    if (this.destroyed || !this.pending) return;
    this.lastRasterAt =
      typeof performance !== "undefined" ? performance.now() : 0;
    void this.rerasterize(this.pending);
  }

  private async rerasterize(messages: ChatMessage[]): Promise<void> {
    if (this.destroyed || !this.sprite) return;
    const gen = ++this.generation;

    const svg = renderChatDesignSvg(this.design, messages);

    try {
      const texture = await rasterizeSvgToTexture(
        svg,
        this.design.cardWidth,
        this.design.cardHeight,
        this.renderScale,
      );

      if (
        this.destroyed ||
        gen !== this.generation ||
        !this.sprite ||
        this.sprite.destroyed
      ) {
        texture.destroy(true);
        return;
      }

      const old = this.texture;
      this.texture = texture;
      this.sprite.texture = texture;
      // Pixi v8 resets the sprite to the texture's intrinsic size on assign —
      // re-assert the widget bounds so the panel always fills `destination`.
      this.sprite.width = this.destWidth;
      this.sprite.height = this.destHeight;
      if (old && old !== Texture.EMPTY) old.destroy(true);
    } catch {
      // Decode failed — keep the previous frame; let a future update retry.
      if (!this.destroyed && gen === this.generation) {
        this.lastKey = null;
      }
    }
  }

  destroy(): void {
    this.destroyed = true;

    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }
    if (this.texture && this.texture !== Texture.EMPTY) {
      try {
        this.texture.destroy(true);
      } catch {
        /* best-effort */
      }
    }
    this.texture = null;
    this.sprite = null;
    this.pending = null;

    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }
}
